import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MasterService } from '../../../../../services/master.service'
import { MessageService } from '../../../../../services/message.service'
import { Subscription } from 'rxjs';
import * as languageLibrary from '../../../../../services/language'
import { Contact } from 'src/app/models/contact.model';

@Component({
    selector: 'general-information',
    templateUrl: './general-information.component.html',
    styleUrls: ['./general-information.component.scss'],
    standalone: false
})
export class GeneralInformationComponent implements OnInit, OnChanges, OnDestroy {

    /*
    * variables
    */
    @Input() contactId: number = 0

    @Input() isIndividual: boolean = true

    @Input() isFlat: boolean = false
    
    @Output() generalInformation$: EventEmitter<Object> = new EventEmitter()

    protected words = languageLibrary.language

    protected language: string = 'EN'
    
    protected contact: Contact | null = null
    
    protected contactGeneralInfo = new FormGroup({
        contactId: new FormControl<number>(0, []),
        isIndividual: new FormControl<boolean>(true),
        firstName: new FormControl<string | null>(null),
        lastName: new FormControl<string | null>(null),
        suffix: new FormControl<string | null>(null),
        middleName: new FormControl<string | null>(null),
        title: new FormControl<string | null>(null),
        birthdate: new FormControl<string | Date | null>(null),
        taxPayerNumber: new FormControl<string | null>(null),
        companyName: new FormControl<string | null>(null)
    })

    private subscriptions: Subscription[] = []

    // Indicates whether or not the contact role is fixed for this list.
    protected fixedRole = false

    // The key of the fixed contact role or null if none.
    protected fixedRoleKey = null

    // The UI route path of the fixed contact role (default: 'contacts').
    protected path = 'contacts'

    get company() { return this.contactGeneralInfo.get('companyName') }
    get first() { return this.contactGeneralInfo.get('firstName') }
    get last() { return this.contactGeneralInfo.get('lastName') }

    /*
    * Life Cycles
    */
    constructor(private router: Router, private route: ActivatedRoute, private master: MasterService, private ms: MessageService, private location: Location) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.contactId) {
            this.contactGeneralInfo.reset()
            this.getContact()
        }
    }

    ngOnInit() {
        const path = this.route.snapshot.routeConfig.path
        const pattern = new RegExp('employee', 'i')
        this.fixedRole = (path && pattern.test(path))
        if (this.fixedRole) {
            this.fixedRoleKey = 'employee'
            this.path = 'employees'
        }

        this.subscriptions.push(
            this.contactGeneralInfo.get('isIndividual').valueChanges.subscribe((individual) => {
                this.isIndividual = individual
                this.modifyValidators()
            })
        )
        
        this.getContact()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    protected getContact() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        this.master.get(`contacts/${this.contactId}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contact) {
                return
            }

            this.contact = res.data.contact
            this.setContactGeneralInfoForm()
        })
    }

    protected setContactGeneralInfoForm = (): void => {
        if (!this.contact) {
            return
        }

        this.isIndividual = this.contact['isIndividual']
        this.contactId = this.contact.id

        this.contactGeneralInfo.setValue({
            contactId: this.contact['id'],
            isIndividual: this.isIndividual,
            firstName: this.contact['firstName'],
            lastName: this.contact['lastName'],
            suffix: this.contact['suffix'],
            middleName: this.contact['middleName'],
            title: this.contact['title'],
            birthdate: this.contact['birthdate'],
            companyName: this.contact['companyName'],
            taxPayerNumber: this.contact['taxPayerNumber']
        })

        this.generalInformation$.emit({ message: 'individualValue', value: this.isIndividual })
        this.modifyValidators()
    }

    protected save(e: Event) {
        e.stopPropagation()
        e.preventDefault()
        
        

        if (!this.isIndividual) {
            this.contactGeneralInfo.get('birthdate').setValue(null)
            this.contactGeneralInfo.get('suffix').setValue(null)
            this.contactGeneralInfo.get('title').setValue(null)
            this.contactGeneralInfo.get('middleName').setValue(null)
        } 
        else {
            this.contactGeneralInfo.get('companyName').setValue(null)
        }

        const identity = this.contactId
        if (!identity || isNaN(identity) || (identity <= 0)) {
            this.createContact()
            return
        }

        this.modifyContact()
    }

    protected createContact() {
        if (this.contactGeneralInfo.invalid) {
            this.ms.sendMessage("alert", { type: "danger", text: 'Please fill out the required fields.' });
            return
        }

        this.master.post(`contacts`, { information: this.contactGeneralInfo.value }, res => {
            if (!res) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.contactId = parseInt(res.data.contact.id)
            this.contactGeneralInfo.get('contactId').setValue(this.contactId)
            this.appendRole()

            this.generalInformation$.emit({ message: 'individualValue', value: this.isIndividual })
            this.generalInformation$.emit({ message: 'setContactId', value: this.contactId })
        })
    }

    protected modifyContact = (): void => {
        this.contactGeneralInfo.get('isIndividual').setValue(this.isIndividual)
        if (this.contactGeneralInfo.invalid) {
            this.ms.sendMessage("alert", { type: "danger", text: 'Please fill out the required fields.' });
            return
        }

        const identity = this.contactGeneralInfo.get('contactId').value
        if (isNaN(identity) || (identity <= 0)) {
            return
        }

        const payload = Object.assign({}, this.contactGeneralInfo.value)

        this.master.put(`contacts/${identity}`, { information: this.contactGeneralInfo.value }, res => {
            if (!res) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.generalInformation$.emit({ message: 'individualValue', value: this.isIndividual })

        })
    }

    /**
     * In situations where the role is fixed and the 
     * contact was rreated we need to set the role.
     */
    private appendRole() {
        if (!this.fixedRole || !this.fixedRoleKey) {
            return
        }

        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        this.master.post(`contacts/${this.contactId}/roles`, { information: this.fixedRoleKey }, res => {
            if (!res) {
              return
            }
      
            if (res.status !== 200) {
              this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
              return
            }
        })
    }

    protected modifyValidators = () => {
        this.contactGeneralInfo.clearValidators()

        if (this.isIndividual) {
            this.contactGeneralInfo.get('firstName').setValidators([Validators.required])
            this.contactGeneralInfo.get('lastName').setValidators([Validators.required])
            this.contactGeneralInfo.get('companyName').setValidators(null)
        }
        else {
            this.contactGeneralInfo.get('firstName').setValidators(null)
            this.contactGeneralInfo.get('lastName').setValidators(null)
            this.contactGeneralInfo.get('companyName').setValidators([Validators.required])
        }

        this.contactGeneralInfo.get('firstName').updateValueAndValidity()
        this.contactGeneralInfo.get('lastName').updateValueAndValidity()
        this.contactGeneralInfo.get('taxPayerNumber').updateValueAndValidity()
        this.contactGeneralInfo.get('companyName').updateValueAndValidity()
    }
}